/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { OrdersListActionProps } from '@/account/types';
import { Icon, Card } from '@adobe-commerce/elsie/components';
import { ChevronRight } from '@adobe-commerce/elsie/icons';
import { classes } from '@adobe-commerce/elsie/lib';
import { checkIsFunction } from '@/account/lib/checkIsFunction';
import { useText } from '@adobe-commerce/elsie/i18n';
import '@/account/components/OrdersListAction/OrdersListAction.css';

interface IconConfig {
  size: '12' | '16' | '24' | '32' | '64' | '80' | undefined;
  stroke: '4' | '1' | '2' | '3' | undefined;
}

const iconConfig: IconConfig = { size: '32', stroke: '2' };

export const OrdersListAction: FunctionComponent<OrdersListActionProps> = ({
  minifiedView,
  orderNumber,
  orderToken,
  routeOrdersList,
  routeOrderDetails,
}) => {
  const minifiedViewKey = minifiedView ? 'minifiedView' : 'fullSizeView';

  const translations = useText({
    viewAllOrdersButton: `Account.${minifiedViewKey}.OrdersList.viewAllOrdersButton`,
    ariaLabelLink: `Account.${minifiedViewKey}.OrdersList.ariaLabelLink`,
  });

  if (routeOrdersList) {
    return (
      <a
        className={classes([
          'account-orders-list-action',
          ['account-orders-list-action--minifiedView', minifiedView],
        ])}
        data-testid="ordersListActionButtonMinifiedView"
        href={routeOrdersList()}
      >
        <Card data-testid="ordersListActionMinifiedView" variant="secondary">
          <div className="account-orders-list-action__card-wrapper">
            <p>{translations.viewAllOrdersButton}</p>

            <Icon source={ChevronRight} {...iconConfig} />
          </div>
        </Card>
      </a>
    );
  }

  return (
    <a
      aria-label={`${translations.ariaLabelLink} ${orderNumber ?? orderToken}`}
      href={
        checkIsFunction(routeOrderDetails)
          ? routeOrderDetails(orderNumber, orderToken)
          : '#'
      }
      className="account-orders-list-action"
      data-testid="ordersListActionButton"
    >
      <Icon source={ChevronRight} {...iconConfig} />
    </a>
  );
};
